import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import type { 
  ClientReceivablesAging,
  ClientBalance,
  ClientBalanceSummary,
  ClientTransaction,
  ClientTransactionOptions,
  CursorPaginationResponse
} from '../../types/Insurance';

// Client receivables aging report
export const useClientReceivablesAging = (asOfDate?: Date) => {
  return useQuery({
    queryKey: ['client-receivables-aging', asOfDate?.toISOString()],
    queryFn: (): Promise<ClientReceivablesAging[]> => apiClient.getClientReceivablesAging(asOfDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
};

// Client balance summary
export const useClientBalanceSummary = (clientId: string, periodId: string) => {
  return useQuery({
    queryKey: ['client-balance-summary', clientId, periodId],
    queryFn: (): Promise<ClientBalance[]> => apiClient.getClientBalanceSummary(clientId, periodId),
    enabled: !!clientId && !!periodId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false
  });
};

// Multi-client balance summary for reports
export const useMultiClientBalanceSummary = (params: { periodId?: string }) => {
  return useQuery({
    queryKey: ['multi-client-balance-summary', params.periodId],
    queryFn: (): Promise<ClientBalanceSummary[]> => apiClient.getMultiClientBalances({ periodId: params.periodId }),
    enabled: !!params.periodId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false
  });
};

// Multi-client balance summaries
export const useMultiClientBalances = (
  clientIds: string[], 
  periodId: string, 
  accountLogicalKeys?: string[]
) => {
  return useQuery({
    queryKey: ['multi-client-balances', clientIds, periodId, accountLogicalKeys],
    queryFn: () => apiClient.getMultiClientBalances({ clientIds, periodId, accountLogicalKeys }),
    enabled: clientIds.length > 0 && !!periodId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false
  });
};

// Client transaction history with cursor pagination
export const useClientTransactionHistory = (
  clientId: string, 
  options: ClientTransactionOptions
) => {
  return useInfiniteQuery({
    queryKey: ['client-transaction-history', clientId, options],
    queryFn: ({ pageParam }): Promise<CursorPaginationResponse<ClientTransaction>> => 
      apiClient.getClientTransactionHistory(clientId, {
        ...options,
        cursor: pageParam as string | undefined
      }),
    initialPageParam: undefined as string | undefined, // Required in React Query v5
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    getPreviousPageParam: (firstPage) => firstPage.pagination.prevCursor, // Support backward pagination
    enabled: !!clientId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false
  });
};
